'use client';

import { createClient } from '@/lib/supabase/client';

export interface VehicleImage {
  id: string;
  url: string;
  alt: string;
  category: 'exterior' | 'interior' | 'features';
  displayOrder: number;
}

export interface VehicleReview {
  id: string;
  userName: string;
  userImage: string | null;
  rating: number;
  comment: string;
  tripType: string | null;
  verified: boolean;
  helpfulCount: number;
  createdAt: string;
}

export interface VehicleSpecifications {
  engine?: string;
  power?: string;
  range?: string;
  acceleration?: string;
  transmission?: string;
  seating?: string;
  cargo?: string;
  safety?: string;
  topSpeed?: string;
  chargingTime?: string;
}

export interface VehicleFeature {
  icon: string;
  label: string;
  category?: string;
}

export interface VehiclePricing {
  dailyRate: number;
  weeklyRate: number;
  monthlyRate: number;
  deposit: number;
  insurance: number;
  mileageLimit: string;
  extraMileageCost: number;
}

export interface VehicleDetails {
  id: string;
  name: string;
  category: string;
  description: string;
  images: VehicleImage[];
  specifications: VehicleSpecifications;
  features: VehicleFeature[];
  pricing: VehiclePricing;
  availability: string;
  availableUnits: number;
  reviews: VehicleReview[];
  averageRating: number;
  totalReviews: number;
  location: string;
}

export interface Vehicle {
  id: string;
  name: string;
  category: string;
  image: string;
  alt: string;
  rating: number;
  reviewCount: number;
  pricePerDay: number;
  originalPrice?: number;
  features: Array<{ icon: string; label: string }>;
  transmission: string;
  fuelType: string;
  seats: number;
  available: boolean;
  availableUnits: number;
  badge?: string;
  location: string;
}

export const vehicleService = {
  async getAllVehicles(): Promise<Vehicle[]> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from('vehicles')
      .select(`
        *,
        vehicle_inventory(*),
        vehicle_pricing(*)
      `);

    if (error) {
      console.error('Error fetching vehicles:', error);
      return [];
    }

    return data?.map((row: any) => ({
      id: row.id,
      name: row.name,
      category: row.category,
      image: row.image,
      alt: row.alt,
      rating: parseFloat(row.rating) || 0,
      reviewCount: row.review_count || 0,
      pricePerDay: parseFloat(row.vehicle_pricing?.[0]?.price_per_day) || 0,
      originalPrice: row.vehicle_pricing?.[0]?.original_price
        ? parseFloat(row.vehicle_pricing[0].original_price)
        : undefined,
      features: row.features || [],
      transmission: row.transmission,
      fuelType: row.fuel_type,
      seats: row.seats,
      available: (row.vehicle_inventory?.[0]?.available_units || 0) > 0,
      availableUnits: row.vehicle_inventory?.[0]?.available_units || 0,
      badge: row.badge,
      location: row.location,
    })) || [];
  },

  async getVehicleById(vehicleId: string): Promise<Vehicle | null> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from('vehicles')
      .select(`
        *,
        vehicle_inventory(*),
        vehicle_pricing(*)
      `)
      .eq('id', vehicleId)
      .single();

    if (error) {
      console.error('Error fetching vehicle:', error);
      return null;
    }

    if (!data) return null;

    return {
      id: data.id,
      name: data.name,
      category: data.category,
      image: data.image,
      alt: data.alt,
      rating: parseFloat(data.rating) || 0,
      reviewCount: data.review_count || 0,
      pricePerDay: parseFloat(data.vehicle_pricing?.[0]?.price_per_day) || 0,
      originalPrice: data.vehicle_pricing?.[0]?.original_price
        ? parseFloat(data.vehicle_pricing[0].original_price)
        : undefined,
      features: data.features || [],
      transmission: data.transmission,
      fuelType: data.fuel_type,
      seats: data.seats,
      available: (data.vehicle_inventory?.[0]?.available_units || 0) > 0,
      availableUnits: data.vehicle_inventory?.[0]?.available_units || 0,
      badge: data.badge,
      location: data.location,
    };
  },

  async getVehicleDetails(vehicleId: string): Promise<VehicleDetails | null> {
    const supabase = createClient();

    // Fetch vehicle with all related data
    const { data: vehicle, error: vehicleError } = await supabase
      .from('vehicles')
      .select(`
        *,
        vehicle_inventory(*),
        vehicle_pricing(*)
      `)
      .eq('id', vehicleId)
      .single();

    if (vehicleError || !vehicle) {
      console.error('Error fetching vehicle:', vehicleError);
      return null;
    }

    // Fetch vehicle images
    const { data: images, error: imagesError } = await supabase
      .from('vehicle_images')
      .select('*')
      .eq('vehicle_id', vehicleId)
      .order('display_order', { ascending: true });

    if (imagesError) {
      console.error('Error fetching vehicle images:', imagesError);
    }

    // Fetch vehicle reviews
    const { data: reviews, error: reviewsError } = await supabase
      .from('vehicle_reviews')
      .select('*')
      .eq('vehicle_id', vehicleId)
      .order('created_at', { ascending: false });

    if (reviewsError) {
      console.error('Error fetching vehicle reviews:', reviewsError);
    }

    const pricingData = vehicle.vehicle_pricing?.[0];
    const inventoryData = vehicle.vehicle_inventory?.[0];

    return {
      id: vehicle.id,
      name: vehicle.name,
      category: vehicle.category,
      description: `Experience the ${vehicle.name}, a premium ${vehicle.category} that combines cutting-edge technology with exceptional performance. Perfect for those who demand the best in comfort, safety, and innovation.`,
      images: (images || []).map((img: any) => ({
        id: img.id,
        url: img.url,
        alt: img.alt,
        category: img.category,
        displayOrder: img.display_order,
      })),
      specifications: vehicle.specifications || {},
      features: vehicle.features || [],
      pricing: {
        dailyRate: parseFloat(pricingData?.price_per_day || 0),
        weeklyRate: parseFloat(pricingData?.weekly_rate || 0),
        monthlyRate: parseFloat(pricingData?.monthly_rate || 0),
        deposit: parseFloat(pricingData?.deposit || 0),
        insurance: parseFloat(pricingData?.insurance || 0),
        mileageLimit: pricingData?.mileage_limit || 'Unlimited',
        extraMileageCost: parseFloat(pricingData?.extra_mileage_cost || 0),
      },
      availability: (inventoryData?.available_units || 0) > 0 ? 'Available Now' : 'Currently Unavailable',
      availableUnits: inventoryData?.available_units || 0,
      reviews: (reviews || []).map((review: any) => ({
        id: review.id,
        userName: review.user_name,
        userImage: review.user_image,
        rating: review.rating,
        comment: review.comment,
        tripType: review.trip_type,
        verified: review.verified,
        helpfulCount: review.helpful_count,
        createdAt: review.created_at,
      })),
      averageRating: parseFloat(vehicle.rating) || 0,
      totalReviews: vehicle.review_count || 0,
      location: vehicle.location,
    };
  },

  subscribeToVehicleUpdates(
    onUpdate: (vehicles: Vehicle[]) => void
  ): () => void {
    const supabase = createClient();

    const channel = supabase
      .channel('vehicle_updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'vehicle_inventory',
        },
        async () => {
          const vehicles = await vehicleService.getAllVehicles();
          onUpdate(vehicles);
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'vehicle_pricing',
        },
        async () => {
          const vehicles = await vehicleService.getAllVehicles();
          onUpdate(vehicles);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },
};